import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/LangContext";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

export const PdfPage = ({ bookId, page, zoom, onError, filePath, onLoad, onFitReady, onPageReady }) => {
  const { t } = useLang();
  const [url, setUrl] = useState(null);
  const [vw, setVw] = useState(() => window.innerWidth);
  const width = Math.min(vw - 56, 680);
  const boxRef = useRef(null);
  const ratioRef = useRef(1.414);

  // Gives the parent a getter: the zoom at which the whole page fits the visible reading area.
  useEffect(() => {
    onFitReady?.(() => {
      const box = boxRef.current;
      const scroller = box?.closest("[data-lenis-prevent]");
      if (!box || !scroller) return 1;
      const top = box.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      const availH = scroller.clientHeight - top - 24;
      const availW = scroller.clientWidth - 32;
      return Math.max(0.2, Math.min(availH / ratioRef.current, availW) / width);
    });
  }, [width]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let objectUrl;
    api.get(filePath || `/books/${bookId}/file`, { responseType: "blob" })
      .then((r) => { objectUrl = URL.createObjectURL(r.data); setUrl(objectUrl); })
      .catch((e) => onError?.(errMsg(e)));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [bookId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!url) return <p className="text-center opacity-70 py-10">{t.reader.loadingFile}</p>;
  return (
    <div ref={boxRef} className="overflow-x-auto" dir="ltr" data-testid="reader-pdf">
      <div className="w-max mx-auto">
      <Document file={url} onLoadSuccess={(d) => onLoad?.(d.numPages)} loading={<p className="opacity-70 py-10">{t.reader.loadingFile}</p>} error={<p className="py-10">{t.reader.pdfError}</p>}>
        <Page pageNumber={page + 1} width={width * zoom} renderTextLayer={false} renderAnnotationLayer={false} className="shadow-lg"
          onLoadSuccess={(p) => { if (p.originalWidth) ratioRef.current = p.originalHeight / p.originalWidth; onPageReady?.(); }} />
      </Document>
      </div>
    </div>
  );
};
